import { useEffect, useMemo } from 'react'
import { RiAddLine } from '@remixicon/react'
import styles from './detail.module.css'

// Keep CMS content and heading IDs, changing only the reading presentation.
export function splitTemplateGuide(html: string) {
  const headings = Array.from(
    html.matchAll(/<h([12])\b([^>]*)>([\s\S]*?)<\/h\1>/gi)
  )
  if (!headings.length) return { intro: html, sections: [] }
  return {
    intro: html.slice(0, headings[0].index),
    sections: headings.map((heading, index) => ({
      id: heading[2].match(/\bid=["']([^"']+)["']/i)?.[1],
      title: heading[3],
      html: html.slice(
        heading.index + heading[0].length,
        headings[index + 1]?.index ?? html.length
      )
    }))
  }
}

export function TemplateGuide({ html }: { html: string }) {
  const guide = useMemo(() => splitTemplateGuide(html), [html])
  useEffect(() => {
    function revealSection() {
      let id: string
      try {
        id = decodeURIComponent(window.location.hash.slice(1))
      } catch {
        return
      }
      if (!id) return
      const target = document.getElementById(id)
      const disclosure = target?.closest('details')
      if (disclosure) {
        disclosure.open = true
        target.scrollIntoView({ block: 'start' })
      }
    }
    revealSection()
    window.addEventListener('hashchange', revealSection)
    return () => window.removeEventListener('hashchange', revealSection)
  }, [html])
  return (
    <section className={styles.guide} aria-labelledby="template-guide-title">
      <h2 id="template-guide-title">About this template</h2>
      {guide.intro && (
        <div
          className={styles.prose}
          dangerouslySetInnerHTML={{ __html: guide.intro }}
        />
      )}
      <div className={styles.chapters}>
        {guide.sections.map((section, index) => (
          <details
            id={section.id}
            key={section.id || index}
            className={styles.chapter}
            open={index === 0}
          >
            <summary>
              <h3 dangerouslySetInnerHTML={{ __html: section.title }} />
              <RiAddLine aria-hidden="true" />
            </summary>
            <div
              className={styles.prose}
              dangerouslySetInnerHTML={{ __html: section.html }}
            />
          </details>
        ))}
      </div>
    </section>
  )
}
